import React, { useMemo, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import useTranslate from '../../../../Hooks/useTranslate';
import { CenteredFlexBox, ColumnFlexBox } from '../../../FlexBox';
import StatTile from '../../charts/StatTile';
import DonutBreakdown from '../../charts/DonutBreakdown';
import ReportCard from '../../charts/ReportCard';
import NoData from './NoData';
import { pct, outcomeLabel } from './reportUtils';

// Flatten the TOTAL level or a per-section counter into one common shape,
// matching the desktop ReportSectionWrapper field mapping (isTotal vs section).
const normalize = (data, isTotal) => {
  if (!data) return null;
  const callDetails = data.callDetails || {};
  const contactable = data.contactableCount || 0;
  const validUnique = isTotal
    ? data.validUniqueCallCount || 0
    : callDetails.validUniqueContacts || 0;
  return {
    loaded: data.totalFiltersCount || 0,
    filtered: data.filteredCount || 0,
    contactable,
    toContact: validUnique > contactable ? 0 : contactable - validUnique,
    filterDetails:
      (isTotal ? data.totalFilterDetails : data.filterDetails) || [],
    outcomeDetails:
      (isTotal ? data.totalCallOutcomesDetails : callDetails.outcomeCounters) ||
      [],
    totalCalls: isTotal ? data.totalCallCount || 0 : callDetails.callCount || 0,
    answered: isTotal
      ? data.answeredTotalCount || 0
      : callDetails.answeredCallCount || 0,
    unanswered: isTotal
      ? data.unansweredTotalCount || 0
      : callDetails.unansweredCallCount || 0,
    uniqueContacts: isTotal
      ? data.uniqueTotalContacts || 0
      : callDetails.uniqueContacts || 0,
    uniqueAnswered: isTotal
      ? data.uniqueAnwseredTotalContacts || 0
      : callDetails.uniqueAnsweredContacts || 0,
    uniqueUnanswered: isTotal
      ? data.uniqueUnansweredTotalContacts || 0
      : callDetails.uniqueUnansweredContacts || 0,
  };
};

const CallDetailRow = function CallDetailRow({
  color,
  label,
  total,
  unique,
  share,
}) {
  return (
    <CenteredFlexBox sx={{ justifyContent: 'space-between', gap: 1 }}>
      <CenteredFlexBox sx={{ gap: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: color,
            flex: 'none',
          }}
        />
        <Typography sx={{ fontSize: 13, color: '#3a3a42' }} noWrap>
          {label}
        </Typography>
      </CenteredFlexBox>
      <CenteredFlexBox sx={{ gap: 1.5, flex: 'none' }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
          {total.toLocaleString()}
        </Typography>
        <Typography
          sx={{ fontSize: 12, color: '#94a3b8', width: 62, textAlign: 'right' }}
        >
          {`${unique.toLocaleString()} · ${share}%`}
        </Typography>
      </CenteredFlexBox>
    </CenteredFlexBox>
  );
};

/**
 * MobileWaveReport — the Wave report rebuilt to mirror the desktop
 * WaveActivitiesReport panels: a TOTAL/section selector, scope counters
 * (LOADED / FILTERED / CONTACTABLE / TO_CONTACT), the call-detail breakdown
 * (TOTAL_CALLS / ANSWERED / UNANSWERED with unique contacts), and the two
 * donuts (scope details + call outcomes). See MOBILE_PLAN.md §F(9).
 */
const MobileWaveReport = function MobileWaveReport({ graphData = {} }) {
  const theme = useTheme();
  const intl = useIntl();
  const translate = useTranslate();
  const mode = theme.palette.mode;

  const label = (name) => outcomeLabel(intl, name);

  // TOTAL + target sections + not-target sections (like the desktop tabs).
  const sections = useMemo(() => {
    const list = [
      {
        key: 'TOTAL',
        title: translate('TOTAL'),
        data: graphData,
        isTotal: true,
      },
    ];
    (graphData?.targetCounters || []).forEach((s, i) =>
      list.push({
        key: `t-${i}`,
        title: s.sectionName,
        data: s,
        isTotal: false,
      })
    );
    (graphData?.notTargetCounters || []).forEach((s, i) =>
      list.push({
        key: `nt-${i}`,
        title: s.sectionName,
        data: s,
        isTotal: false,
      })
    );
    return list;
  }, [graphData]);

  const [selectedKey, setSelectedKey] = useState('TOTAL');
  const selected = sections.find((s) => s.key === selectedKey) || sections[0];
  const section = normalize(selected?.data, selected?.isTotal);

  const donut = (details, accessor) => {
    const items = (details || [])
      .filter((d) => (d.count || 0) > 0)
      .map((d) => ({ name: accessor(d), count: d.count }));
    const total = items.reduce((acc, d) => acc + d.count, 0);
    const colors = theme.custom.generateConsistentUniqueColors(
      items.map((d) => d.name)
    );
    return {
      total,
      items: items
        .map((d, i) => ({
          label: label(d.name),
          value: pct(d.count, total),
          color: colors[i]?.[mode] ?? colors[i],
        }))
        .sort((a, b) => b.value - a.value),
    };
  };

  const scopeDonut = donut(section?.filterDetails, (d) => d.filterName);
  const outcomeDonut = donut(section?.outcomeDetails, (d) => d.name);

  if (!section) return null;

  return (
    <>
      {sections.length > 1 ? (
        <Box
          className="scr"
          sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}
        >
          {sections.map((s) => (
            <Chip
              key={s.key}
              label={label(s.title)}
              clickable
              onClick={() => setSelectedKey(s.key)}
              color={s.key === selectedKey ? 'primary' : 'default'}
              variant={s.key === selectedKey ? 'filled' : 'outlined'}
              sx={{ flex: 'none' }}
            />
          ))}
        </Box>
      ) : null}

      <CenteredFlexBox sx={{ gap: 1.25, alignItems: 'stretch' }}>
        <StatTile
          label={translate('LOADED')}
          value={section.loaded.toLocaleString()}
        />
        <StatTile
          label={translate('FILTERED')}
          value={section.filtered.toLocaleString()}
        />
      </CenteredFlexBox>
      <CenteredFlexBox sx={{ gap: 1.25, alignItems: 'stretch' }}>
        <StatTile
          label={translate('CONTACTABLE')}
          value={section.contactable.toLocaleString()}
        />
        <StatTile
          label={translate('TO_CONTACT')}
          value={section.toContact.toLocaleString()}
        />
      </CenteredFlexBox>

      <ReportCard title={translate('CALL_OUTCOMES')}>
        <ColumnFlexBox sx={{ gap: 1.25 }}>
          <CallDetailRow
            color="#4fc3f7"
            label={translate('TOTAL_CALLS')}
            total={section.totalCalls}
            unique={section.uniqueContacts}
            share={100}
          />
          <CallDetailRow
            color="#81c784"
            label={translate('ANSWERED')}
            total={section.answered}
            unique={section.uniqueAnswered}
            share={pct(section.uniqueAnswered, section.uniqueContacts)}
          />
          <CallDetailRow
            color="#e57373"
            label={translate('UNANSWERED')}
            total={section.unanswered}
            unique={section.uniqueUnanswered}
            share={pct(section.uniqueUnanswered, section.uniqueContacts)}
          />
        </ColumnFlexBox>
      </ReportCard>

      <ReportCard title={translate('SCOPE_DETAILS')}>
        {scopeDonut.items.length ? (
          <DonutBreakdown
            items={scopeDonut.items}
            centerValue={scopeDonut.total.toLocaleString()}
            centerLabel={translate('TOTAL')}
          />
        ) : (
          <NoData />
        )}
      </ReportCard>

      <ReportCard title={translate('CALL_OUTCOMES')}>
        {outcomeDonut.items.length ? (
          <DonutBreakdown
            items={outcomeDonut.items}
            centerValue={(
              section.totalCalls || outcomeDonut.total
            ).toLocaleString()}
            centerLabel={translate('TOTAL')}
          />
        ) : (
          <NoData />
        )}
      </ReportCard>
    </>
  );
};

export default MobileWaveReport;
